// ============================================================
// Page de connexion Super Admin — Lot L04 du plan SaaS
// ============================================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../context/SuperAdminAuthContext';
import { AlertCircle, Eye, EyeOff, X, Info, CheckCircle2, AlertTriangle, Loader, Mail, MessageSquare } from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import FooterMentions from '../../components/common/FooterMentions';
import { resetFavicon } from '../../utils/favicon';

// ── Modal « Mot de passe oublié » (email OU SMS) — Super Admin ───
function ForgotModal({ onClose, identifiantInitial }) {
  const [etape, setEtape] = useState('saisie');   // 'saisie' | 'sms' | 'fait'
  const [identifiant, setIdentifiant] = useState(identifiantInitial || '');
  const [canal, setCanal] = useState('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [infoType, setInfoType] = useState('email');

  const [code, setCode] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');

  const demander = async (e) => {
    e.preventDefault();
    setError('');
    if (!identifiant.trim()) return setError('Saisis ton email ou ton numéro de téléphone.');
    setLoading(true);
    try {
      const res = await superAdminApi.post('/superadmin/auth/forgot-password', { identifiant: identifiant.trim(), canal });
      if (res.data.method === 'sms') {
        setEtape('sms'); setInfo(res.data.message);
      } else {
        setInfoType(res.data.method === 'unavailable' ? 'admin' : 'email');
        setInfo(res.data.message); setEtape('fait');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue. Réessaie.');
    } finally { setLoading(false); }
  };

  const validerCode = async (e) => {
    e.preventDefault();
    setError('');
    if (code.trim().length < 4) return setError('Saisis le code reçu par SMS.');
    if (pwd.length < 8) return setError('Le mot de passe doit faire au moins 8 caractères.');
    if (pwd !== pwd2) return setError('Les deux mots de passe ne correspondent pas.');
    setLoading(true);
    try {
      const res = await superAdminApi.post('/superadmin/auth/reset-password-sms', {
        identifiant: identifiant.trim(), code: code.trim(), nouveauMotDePasse: pwd,
      });
      setInfoType('sms'); setInfo(res.data.message || 'Mot de passe réinitialisé.'); setEtape('fait');
    } catch (err) {
      setError(err.response?.data?.message || 'Code incorrect ou expiré.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog" aria-modal="true" aria-label="Mot de passe oublié" onClick={onClose}>
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">Mot de passe oublié</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Fermer"><X size={20} /></button>
        </div>

        {etape === 'fait' ? (
          <div className="text-center py-2">
            {infoType === 'admin'
              ? <Info size={40} className="mx-auto text-blue-500 mb-3" />
              : <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />}
            <p className="text-slate-600 text-sm">{info}</p>
            <button onClick={onClose} className="mt-5 w-full bg-slate-800 hover:bg-slate-900 text-white py-2.5 rounded-lg transition">Compris</button>
          </div>
        ) : etape === 'sms' ? (
          <form onSubmit={validerCode} className="space-y-4">
            <p className="text-slate-500 text-sm">{info}</p>
            <div>
              <label className="block text-sm text-slate-500 mb-1.5">Code reçu par SMS</label>
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric" placeholder="6 chiffres" autoFocus
                className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 tracking-widest text-center text-lg focus:outline-none focus:border-blue-600" />
            </div>
            <div>
              <label className="block text-sm text-slate-500 mb-1.5">Nouveau mot de passe</label>
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Min. 8 caractères"
                className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-600" autoComplete="new-password" />
            </div>
            <div>
              <label className="block text-sm text-slate-500 mb-1.5">Confirmer</label>
              <input type="password" value={pwd2} onChange={e => setPwd2(e.target.value)} placeholder="Re-saisis le mot de passe"
                className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-600" autoComplete="new-password" />
            </div>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
            <button type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition disabled:opacity-50">
              {loading && <Loader size={16} className="animate-spin" />}
              {loading ? 'Validation…' : 'Réinitialiser'}
            </button>
            <button type="button" onClick={() => { setEtape('saisie'); setError(''); }} className="w-full text-xs text-slate-400 hover:text-slate-600">← Recommencer</button>
          </form>
        ) : (
          <form onSubmit={demander} className="space-y-4">
            <p className="text-slate-500 text-sm">
              Saisis ton email ou ton numéro de téléphone, puis choisis le canal de réinitialisation.
            </p>
            <input value={identifiant} onChange={e => setIdentifiant(e.target.value)}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-600"
              placeholder="email@youtech.tech ou 6xx xx xx xx" required autoFocus />
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setCanal('email')}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm border transition ${canal === 'email' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}>
                <Mail size={15} /> Par email
              </button>
              <button type="button" onClick={() => setCanal('sms')}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm border transition ${canal === 'sms' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}>
                <MessageSquare size={15} /> Par SMS
              </button>
            </div>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
            <button type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition disabled:opacity-50">
              {loading && <Loader size={16} className="animate-spin" />}
              {loading ? 'Envoi…' : 'Envoyer'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function SuperAdminLogin() {
  const { login, login2fa } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [identifiant, setIdentifiant] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null); // challengeToken si 2FA requise
  const [code, setCode] = useState('');
  const [sessionExpiree, setSessionExpiree] = useState(false);

  // Identité neutre de la console : titre + favicon par défaut (pas celui
  // d'un commerce éventuellement gardé en cache par une session précédente).
  useEffect(() => {
    document.title = 'Console Super Admin';
    resetFavicon();
    try {
      if (sessionStorage.getItem('sv_sa_session_expiree')) {
        setSessionExpiree(true);
        sessionStorage.removeItem('sv_sa_session_expiree');
      }
    } catch { /* ignore */ }
  }, []);

  const majHandler = (e) => {
    if (typeof e.getModifierState === 'function') setCapsOn(e.getModifierState('CapsLock'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Étape 2 : code 2FA
      if (mfaChallenge) {
        if (code.trim().length < 6) { setError('Saisis le code.'); setLoading(false); return; }
        await login2fa(mfaChallenge, code.trim());
        navigate('/superadmin/commerces');
        return;
      }
      // Étape 1 : identifiants
      const result = await login(identifiant.trim(), password);
      if (result?.mfaRequired) {
        setMfaChallenge(result.challengeToken);
        setError('');
        return;
      }
      navigate('/superadmin/commerces');
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response?.data?.message || 'Trop de tentatives. Réessaie dans quelques minutes.');
      } else {
        setError(err.response?.data?.message || (mfaChallenge ? 'Code invalide' : 'Erreur de connexion'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logos-gc/app-icon-192.png" alt="Gestion Commerce"
            className="inline-block w-16 h-16 rounded-2xl border border-blue-200 object-contain bg-white mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Console Super Admin</h1>
          <p className="text-slate-400 text-sm mt-1">Plateforme de Gestion des Commerces</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" noValidate>
          {sessionExpiree && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2.5 rounded-lg text-sm" role="status">
              <AlertTriangle size={16} className="flex-shrink-0 text-amber-500" />
              <span>Votre session a expiré (inactivité). Reconnectez-vous.</span>
            </div>
          )}
          {mfaChallenge ? (
            <div>
              <label htmlFor="sa-2fa" className="block text-sm text-slate-500 mb-1.5">Code de vérification (2FA)</label>
              <input id="sa-2fa" value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z-]/g, '').slice(0, 9))}
                inputMode="numeric" autoFocus
                className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 text-center tracking-[0.4em] text-lg focus:outline-none focus:border-blue-600"
                placeholder="000000" />
              <p className="mt-1.5 text-xs text-slate-400">
                Code de ton application d'authentification, ou un code de secours.
              </p>
            </div>
          ) : (
          <>
          <div>
            <label htmlFor="sa-id" className="block text-sm text-slate-500 mb-1.5">Email ou téléphone</label>
            <input id="sa-id" type="text" value={identifiant} onChange={(e) => setIdentifiant(e.target.value)}
              required autoFocus autoComplete="username"
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-600"
              placeholder="email@youtech.tech ou 6xx xx xx xx" />
          </div>
          <div>
            <label htmlFor="sa-password" className="block text-sm text-slate-500 mb-1.5">Mot de passe</label>
            <div className="relative">
              <input id="sa-password" type={showPwd ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} onKeyUp={majHandler} onKeyDown={majHandler}
                required autoComplete="current-password"
                className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:border-blue-600" />
              <button type="button" onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {capsOn && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                <AlertTriangle size={13} /> La touche Verr. Maj (Caps Lock) est activée.
              </p>
            )}
          </div>
          </>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm" role="alert">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50">
            {loading && <Loader size={16} className="animate-spin" />}
            {loading ? 'Connexion…' : (mfaChallenge ? 'Valider le code' : 'Se connecter')}
          </button>

          {mfaChallenge ? (
            <p className="text-center text-sm text-slate-500 pt-1">
              <button type="button" onClick={() => { setMfaChallenge(null); setCode(''); setError(''); }}
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
                ← Retour à la connexion
              </button>
            </p>
          ) : (
            /* Mot de passe oublié — sous le bouton de connexion */
            <p className="text-center text-sm text-slate-500 pt-1">
              Vous avez oublié votre mot de passe ?{' '}
              <button type="button" onClick={() => setShowForgot(true)}
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
                Réinitialiser
              </button>
            </p>
          )}
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          Cet espace est réservé au personnel de la plateforme. Les utilisateurs des commerces utilisent <a href="/login" className="text-blue-600 hover:underline">la page de connexion principale</a>.
        </p>

        <FooterMentions variant="light" className="mt-6" />
      </div>

      {showForgot && (
        <ForgotModal identifiantInitial={identifiant} onClose={() => setShowForgot(false)} />
      )}
    </div>
  );
}
